import { Button, Table } from '@flos/react-ui';
import type { CoverageDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/quote';
import { useHippoText } from '../../hooks/useHippoText';
import CoverageTableRow from '../CoverageTable/CoverageTableRow';
import usePageNavigation from '../../hooks/usePageNavigation';
import type { DependentCoverageDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/opc';
import { LOB_CODE } from '../../constants/coverable';
import ExcludedCoverageAddButton from './ExcludedCoverageAddButton';
import useWindowSize from '../../hooks/useWindowSize';
import { IS_OPC_ADD_AVAILABLE, OPC_ADD_LOBS } from '../../pages/OpcEntryPage/config/OpcConfig';
import { allCoverablesAtom } from '../../recoil/atoms/allCoverablesAtom';
import { useRecoilValue } from 'recoil';
import {
    getCoverableById,
    getCoverableByIdAndFlavourName,
    getProductName
} from '../../utils/coverablesUtil';
import type { CarDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/mvp';
import { convertLocalDateDTOToDate } from '../../utils/dateUtil';
import type { PrhHouseDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/prh';
import { sortedCoverablesListSelector } from '../../recoil/selectors/sortedCoverablesListSelector';
import {
    PRH_ROT_AND_INSECTS_COV,
    PRH_WATER_DAMAGE_COV,
    ACC_DEATH_COVERAGE,
    MVPLEASINGCARRETURN,
    MVPCOMPREHENSIVE_COV
} from '../../constants/coverage';
import type { AdultAccidentDTO } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/onlinesales/dto/accident';
import type { CarDTO_Top } from 'gw-edge-client/dist/types/dto/pc/top/edge/capabilities/policyjob/lob/mvp/coverables/topdanmark/dto';

const ROOF_AGE_LIMIT_19_YEARS = 19;
const ROOF_AGE_LIMIT_29_YEARS = 29;
const DOORS_WINDOWS_AGE_LIMIT = 29;
const BUILT_UP_ROOF_TYPE = 'Built up, fladt tag';

const isExclusionCoverageSelected = (
    coverage: CoverageDTO,
    includedCoverages: CoverageDTO[]
): boolean => {
    const exclusionCoverages = coverage.exclusionCoverage ?? [];
    return exclusionCoverages.some((exCov) =>
        includedCoverages.some((inCov) => inCov.type === exCov.coverageId)
    );
};

const ExcludedCoverages = ({
    shouldRenderTitle = false,
    showCoverableDetailsUI,
    excludedCoverages,
    flavourName,
    coverableId,
    includedCoverages = []
}: {
    shouldRenderTitle?: boolean;
    showCoverableDetailsUI: boolean;
    excludedCoverages: CoverageDTO[] | undefined;
    flavourName: string;
    coverableId: string;
    includedCoverages: CoverageDTO[] | undefined;
}): React.JSX.Element => {
    const getHippoText = useHippoText();
    const { goToOPCAddCoverageModal } = usePageNavigation();
    const { isMobileDevice } = useWindowSize();
    const { sortedCoverables } = useRecoilValue(sortedCoverablesListSelector);
    const coverable = getCoverableByIdAndFlavourName(sortedCoverables, coverableId, flavourName);

    const isCallMeModalRequiredForCoverage = (coverage: CoverageDTO): boolean => {
        const callMeModalCoverages = [ACC_DEATH_COVERAGE, MVPLEASINGCARRETURN];
        if (callMeModalCoverages.includes(coverage.type ?? '')) return true;
        return shouldCallMePRH(coverage) || shouldCallMeCar(coverage);
    };

    const isCallMeModalRequiredForCoverable = (): boolean => {
        if (coverable && coverable.lobCode === LOB_CODE.MVP) {
            const car = coverable as CarDTO_Top;
            return !!car.storaged;
        }
        return false;
    };

    const coverables = useRecoilValue(allCoverablesAtom);
    const isParentDependencyCoverageSelected = (
        dependentCoverage: DependentCoverageDTO
    ): boolean => {
        const existInExcludedCoverages = excludedCoverages?.some(
            (coverage) => coverage.type === dependentCoverage.coverageId
        );
        return !existInExcludedCoverages;
    };

    const shouldCallMeCar = (coverage: CoverageDTO): boolean => {
        const car = coverable as CarDTO;
        if (!car || car.lobCode !== LOB_CODE.MVP || car.fixedVehicleAge === undefined) {
            return false;
        }
        return car.fixedVehicleAge > 10 && coverage.type === MVPCOMPREHENSIVE_COV;
    };

    const shouldCallMePRH = (coverage: CoverageDTO): boolean => {
        const house = coverable as PrhHouseDTO;
        if (!house || house.lobCode !== LOB_CODE.PRIVATE_HOUSE) {
            return false;
        }
        const constructionYear = house.constructionYear;
        const currentYear = new Date().getFullYear();
        if (coverage.type === PRH_ROT_AND_INSECTS_COV) {
            return currentYear - constructionYear > DOORS_WINDOWS_AGE_LIMIT;
        }
        if (coverage.type === PRH_WATER_DAMAGE_COV) {
            return (
                currentYear - constructionYear >
                (house.roofType === BUILT_UP_ROOF_TYPE
                    ? ROOF_AGE_LIMIT_19_YEARS
                    : ROOF_AGE_LIMIT_29_YEARS)
            );
        }
        return false;
    };
    const getActionForExcludedCoverages = (coverage: CoverageDTO): React.JSX.Element => {
        //  If line is workers compensation (WOC) or Agriculture content (CTP) the button should not be visible
        const lineIdentifier = coverage.type?.substring(0, 3);
        if (lineIdentifier === 'WOC' || lineIdentifier === 'CTP' || showCoverableDetailsUI)
            return <></>;

        const productNameInfo = getProductName(flavourName);

        if (
            !coverage.selectable &&
            (productNameInfo === 'top_prh_house' || productNameInfo === 'top_prh_holiday_home')
        ) {
            return <></>;
        }

        const dependentParentCoverage =
            coverage.dependentParentCoverage && coverage.dependentParentCoverage.length > 0
                ? coverage.dependentParentCoverage[0]
                : undefined;

        if (coverage.declinedByTopdanmark_Top)
            return <span>{getHippoText('DSS-SHOPPING-BASKET-MISSING-COVERAGES-UNAVAILABLE')}</span>;

        if (
            !(IS_OPC_ADD_AVAILABLE && OPC_ADD_LOBS.includes(productNameInfo)) &&
            flavourName !== 'Ejerskifteforsikring'
        )
            return <></>;

        if (
            dependentParentCoverage &&
            !isParentDependencyCoverageSelected(dependentParentCoverage)
        ) {
            return (
                <Button theme="secondary" disabled>
                    {getHippoText('DSS-SHOPPING-BASKET-MISSING-COVERAGERS-DEPENDENCY', {
                        denpendent_coverage_title: dependentParentCoverage.coverageName
                    })}
                </Button>
            );
        }

        if (
            isCallMeModalRequiredForCoverable() ||
            isExclusionCoverageSelected(coverage, includedCoverages) ||
            isCallMeModalRequiredForCoverage(coverage)
        ) {
            return <ExcludedCoverageAddButton coverage={coverage} />;
        }

        return (
            <Button
                minWidth="widest"
                onClick={() => {
                    goToOPCAddCoverageModal(productNameInfo, coverableId, coverage.type);
                }}
            >
                {getHippoText('DSS-SHOPPING-BASKET-MISSING-COVERAGES-ADD')}
            </Button>
        );
    };

    const isDateFourYearsAfterFirstRegDateOrVehicleYear = (
        firstRegDate: Date,
        leasingStartDate: Date,
        vehicleYear: number
    ): boolean => {
        const fourYearsAfterFirstRegDate = firstRegDate.setFullYear(firstRegDate.getFullYear() + 4);
        if (leasingStartDate.getTime() > fourYearsAfterFirstRegDate) return true;
        if (leasingStartDate.getFullYear() > vehicleYear + 4) return true;

        return false;
    };

    const filterExcludedCoverages = (): CoverageDTO[] | undefined => {
        const coverable = getCoverableById(coverables, coverableId);
        const lobCode = coverable?.lobCode;
        switch (lobCode) {
            case 'MVP': {
                const car = coverable as CarDTO;
                if (
                    car.leasingStartDate !== undefined &&
                    isDateFourYearsAfterFirstRegDateOrVehicleYear(
                        convertLocalDateDTOToDate(car.firstRegistrationDate),
                        convertLocalDateDTOToDate(car.leasingStartDate),
                        car.year
                    )
                ) {
                    return excludedCoverages?.filter(
                        (cov) => cov.publicID !== 'MVPLeasingcarReturn'
                    );
                }
                break;
            }
            case 'ACC': {
                const accCoverable = coverable as AdultAccidentDTO;
                const insuredPersonAge = accCoverable.age ?? 0;
                if (insuredPersonAge >= 70)
                    return excludedCoverages?.filter(
                        (cov) => cov.publicID !== 'ACCImmediateCompensationCov'
                    );
                break;
            }
            default:
                break;
        }
        return excludedCoverages;
    };

    if (excludedCoverages?.length === 0) {
        return <></>;
    }
    return (
        <>
            <div className="excluded-coverages-container">
                {shouldRenderTitle && (
                    <div className="u-flex-box u-flex-box--space-between">
                        <h4>{getHippoText('DSS-SHOPPING-BASKET-MISSING-COVERAGES-TITLE')}</h4>
                    </div>
                )}
                <Table mobileDirection="vertical" wrapped={isMobileDevice}>
                    <Table.Body>
                        {filterExcludedCoverages()?.map((coverage) => {
                            return (
                                <CoverageTableRow
                                    coverage={coverage}
                                    iconProps={
                                        !showCoverableDetailsUI
                                            ? {
                                                  shape: 'circle-close',
                                                  className: 'has-fill is-grey'
                                              }
                                            : undefined
                                    }
                                    showValueCell={!showCoverableDetailsUI}
                                    key={coverage.name}
                                    showPrice={true}
                                    showActionButton={getActionForExcludedCoverages(coverage)}
                                />
                            );
                        })}
                    </Table.Body>
                </Table>
            </div>
        </>
    );
};

export default ExcludedCoverages;
